import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Leaf, Zap, Clock, Activity, Sun, Fish, ShieldCheck, CheckCircle2, ArrowRight, X } from 'lucide-react';
const Modal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => <AnimatePresence data-id="element-1">
    {isOpen && <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-2">
        <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} transition={{
      duration: 0.2
    }} className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-8 relative" onClick={e => e.stopPropagation()} data-id="element-3">
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-sage-50 text-earth-800 transition-colors" data-id="element-4">
            <X className="w-5 h-5" data-id="element-5" />
          </button>
          <h3 className="font-serif text-2xl font-semibold text-earth-900 mb-6" data-id="element-6">
            {title}
          </h3>
          <div className="text-earth-800 text-sm leading-relaxed space-y-4" data-id="element-7">
            {children}
          </div>
        </motion.div>
      </motion.div>}
  </AnimatePresence>;
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} data-id="element-8">
    {children}
  </motion.div>;
export function SupplementArticle() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  return <article className="min-h-screen bg-cream font-sans text-earth-900 selection:bg-sage-200" data-id="element-9">
      {/* Hero Section */}
      <header className="pt-24 pb-16 px-6 md:pt-32 md:pb-24 max-w-4xl mx-auto text-center" data-id="element-10">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} data-id="element-11">
          <div className="flex justify-center mb-6" data-id="element-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-sage-600 border-b border-sage-300 pb-2" data-id="element-13">
              Wellness Guide
            </span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-semibold leading-tight mb-6 text-earth-900" data-id="element-14">
            Why Do We Need <br className="hidden md:block" data-id="element-15" />
            <span className="italic text-sage-700" data-id="element-16">Supplements</span> Today?
          </h1>
          <p className="text-lg md:text-xl text-earth-800 max-w-2xl mx-auto font-light leading-relaxed" data-id="element-17">
            A practical guide to understanding why modern nutrition is not
            always enough.
          </p>
        </motion.div>
      </header>

      {/* Main Content Container */}
      <main className="max-w-3xl mx-auto px-6 pb-32" data-id="element-18">
        {/* Introduction */}
        <FadeIn data-id="element-19">
          <section className="mb-20 relative" data-id="element-20">
            <div className="absolute -left-4 top-0 bottom-0 w-1 bg-sage-200 rounded-full hidden md:block" data-id="element-21"></div>
            <div className="prose prose-lg md:prose-xl text-earth-800 font-light leading-relaxed" data-id="element-22">
              <p className="first-letter:text-6xl first-letter:font-serif first-letter:font-bold first-letter:text-sage-700 first-letter:mr-3 first-letter:float-left" data-id="element-23">
                For years, we've heard the same phrase: "If you eat well, you
                don't need supplements." In theory, that sounds reasonable. But
                the reality of the 21st century is far more complex.
              </p>
              <p className="mt-6" data-id="element-24">
                We no longer live in the same world as our grandparents. Our
                soils are depleted, stress has become constant, and our
                lifestyles have changed dramatically.
              </p>
              <p className="mt-6 font-medium text-earth-900" data-id="element-25">
                This bonus was created to explain, in a simple way, why
                well-chosen supplements have become an essential support tool
                for people who want to protect their health in the long term.
              </p>
            </div>
          </section>
        </FadeIn>

        <div className="w-full h-px bg-gradient-to-r from-transparent via-sage-300 to-transparent my-16" data-id="element-26"></div>

        {/* Section 1 */}
        <FadeIn data-id="element-27">
          <section className="mb-24 group" data-id="element-28">
            <div className="flex items-center gap-4 mb-6" data-id="element-29">
              <span className="font-serif text-5xl text-sage-300 group-hover:text-sage-500 transition-colors" data-id="element-30">
                01
              </span>
              <h2 className="font-serif text-2xl md:text-3xl font-semibold text-earth-900" data-id="element-31">
                Food Has Changed{' '}
                <span className="text-sage-600 italic font-normal" data-id="element-32">
                  (Depleted Soil)
                </span>
              </h2>
            </div>
            <div className="pl-0 md:pl-16" data-id="element-33">
              <p className="text-lg leading-relaxed text-earth-800 mb-8" data-id="element-34">
                Even if you buy the same apple people bought 50 years ago, its
                mineral content is no longer the same. Intensive farming and the
                use of chemical fertilizers have depleted the soil's natural
                nutrients.
              </p>
              <div className="space-y-6" data-id="element-35">
                <div className="flex gap-4 p-6 bg-white rounded-2xl shadow-sm border border-sage-100" data-id="element-36">
                  <div className="flex-shrink-0 mt-1" data-id="element-37">
                    <div className="w-10 h-10 rounded-full bg-sage-50 flex items-center justify-center text-sage-600" data-id="element-38">
                      <Leaf className="w-5 h-5" data-id="element-39" />
                    </div>
                  </div>
                  <div data-id="element-40">
                    <h3 className="font-semibold text-earth-900 mb-1" data-id="element-41">
                      Poor soil:
                    </h3>
                    <p className="text-earth-800" data-id="element-42">
                      If the mineral is not in the soil, it does not make it to
                      your plate.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 p-6 bg-white rounded-2xl shadow-sm border border-sage-100" data-id="element-43">
                  <div className="flex-shrink-0 mt-1" data-id="element-44">
                    <div className="w-10 h-10 rounded-full bg-sage-50 flex items-center justify-center text-sage-600" data-id="element-45">
                      <Clock className="w-5 h-5" data-id="element-46" />
                    </div>
                  </div>
                  <div data-id="element-47">
                    <h3 className="font-semibold text-earth-900 mb-1" data-id="element-48">
                      Early harvesting:
                    </h3>
                    <p className="text-earth-800" data-id="element-49">
                      Many fruits ripen in trucks or storage chambers,
                      preventing them from developing their full vitamin
                      profile.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </FadeIn>

        {/* Section 2 */}
        <FadeIn data-id="element-50">
          <section className="mb-24 group" data-id="element-51">
            <div className="flex items-center gap-4 mb-6" data-id="element-52">
              <span className="font-serif text-5xl text-sage-300 group-hover:text-sage-500 transition-colors" data-id="element-53">
                02
              </span>
              <h2 className="font-serif text-2xl md:text-3xl font-semibold text-earth-900" data-id="element-54">
                Modern Life: Fast Eating and Constant Stress
              </h2>
            </div>
            <div className="pl-0 md:pl-16" data-id="element-55">
              <div className="prose prose-lg text-earth-800 leading-relaxed" data-id="element-56">
                <p data-id="element-57">
                  Even if your diet is "perfect," chronic stress steals
                  nutrients. When the body is in alert mode, it uses{' '}
                  <strong className="text-earth-900 font-semibold" data-id="element-58">
                    B vitamins
                  </strong>{' '}
                  and{' '}
                  <strong className="text-earth-900 font-semibold" data-id="element-59">
                    Magnesium
                  </strong>{' '}
                  much faster than normal.
                </p>
                <div className="my-8 p-6 border-l-4 border-sage-400 bg-sage-50 rounded-r-2xl" data-id="element-60">
                  <div className="flex items-start gap-4" data-id="element-61">
                    <Zap className="w-6 h-6 text-sage-600 flex-shrink-0 mt-1" data-id="element-62" />
                    <p className="m-0 text-earth-900 italic" data-id="element-63">
                      On top of that, eating while standing, distracted, or in a
                      rush can interfere with digestion, making it harder to
                      properly absorb the nutrients from the healthy foods you
                      buy.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </FadeIn>

        {/* Section 3 */}
        <FadeIn data-id="element-64">
          <section className="mb-24 group" data-id="element-65">
            <div className="flex items-center gap-4 mb-6" data-id="element-66">
              <span className="font-serif text-5xl text-sage-300 group-hover:text-sage-500 transition-colors" data-id="element-67">
                03
              </span>
              <h2 className="font-serif text-2xl md:text-3xl font-semibold text-earth-900" data-id="element-68">
                Age and Absorption
              </h2>
            </div>
            <div className="pl-0 md:pl-16" data-id="element-69">
              <p className="text-lg leading-relaxed text-earth-800 mb-6" data-id="element-70">
                As we move past age 35 or 40, our ability to produce stomach
                acid and digestive enzymes begins to decline. This means we may
                be eating nutrients, but the body is simply not "capturing" them
                efficiently.
              </p>
              <p className="text-lg leading-relaxed text-earth-800 bg-white p-6 rounded-2xl border border-sage-100 shadow-sm" data-id="element-71">
                Critical nutrients such as{' '}
                <span className="font-semibold text-sage-700 bg-sage-50 px-2 py-1 rounded" data-id="element-72">
                  Vitamin B12
                </span>{' '}
                and{' '}
                <span className="font-semibold text-sage-700 bg-sage-50 px-2 py-1 rounded" data-id="element-73">
                  Iron
                </span>{' '}
                may become harder to absorb naturally over time.
              </p>
            </div>
          </section>
        </FadeIn>

        {/* Section 4 */}
        <FadeIn data-id="element-74">
          <section className="mb-24 group" data-id="element-75">
            <div className="flex items-center gap-4 mb-6" data-id="element-76">
              <span className="font-serif text-5xl text-sage-300 group-hover:text-sage-500 transition-colors" data-id="element-77">
                04
              </span>
              <h2 className="font-serif text-2xl md:text-3xl font-semibold text-earth-900" data-id="element-78">
                Silent Deficiencies{' '}
                <span className="text-sage-600 italic font-normal" data-id="element-79">
                  (When Food Is Not Enough)
                </span>
              </h2>
            </div>
            <div className="pl-0 md:pl-16" data-id="element-80">
              <p className="text-lg leading-relaxed text-earth-800 mb-8" data-id="element-81">
                There are certain nutrients that are extremely difficult to
                obtain from food alone at the optimal levels the body may need
                today:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-82">
                {/* Vitamin D Card */}
                <div className="bg-white p-6 rounded-2xl border border-sage-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-83">
                  <div className="w-12 h-12 rounded-full bg-yellow-50 flex items-center justify-center text-yellow-600 mb-4" data-id="element-84">
                    <Sun className="w-6 h-6" data-id="element-85" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-earth-900 mb-3" data-id="element-86">
                    Vitamin D
                  </h3>
                  <p className="text-earth-800 text-sm leading-relaxed" data-id="element-87">
                    Almost impossible to get in adequate amounts from diet
                    alone; it requires sun exposure, which many people do not
                    get because of indoor work.
                  </p>
                </div>

                {/* Magnesium Card */}
                <div className="bg-white p-6 rounded-2xl border border-sage-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-88">
                  <div className="w-12 h-12 rounded-full bg-sage-50 flex items-center justify-center text-sage-600 mb-4" data-id="element-89">
                    <Activity className="w-6 h-6" data-id="element-90" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-earth-900 mb-3" data-id="element-91">
                    Magnesium
                  </h3>
                  <p className="text-earth-800 text-sm leading-relaxed" data-id="element-92">
                    It is estimated that more than 70% of the population has
                    insufficient levels due to its reduced presence in modern
                    soils.
                  </p>
                </div>

                {/* Omega-3 Card */}
                <div className="bg-white p-6 rounded-2xl border border-sage-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-93">
                  <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 mb-4" data-id="element-94">
                    <Fish className="w-6 h-6" data-id="element-95" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-earth-900 mb-3" data-id="element-96">
                    Omega-3
                  </h3>
                  <p className="text-earth-800 text-sm leading-relaxed" data-id="element-97">
                    Unless you eat high-quality fatty fish 3 or 4 times per
                    week, your levels may be low.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </FadeIn>

        {/* Section 5 */}
        <FadeIn data-id="element-98">
          <section className="mb-24 group" data-id="element-99">
            <div className="flex items-center gap-4 mb-6" data-id="element-100">
              <span className="font-serif text-5xl text-sage-300 group-hover:text-sage-500 transition-colors" data-id="element-101">
                05
              </span>
              <h2 className="font-serif text-2xl md:text-3xl font-semibold text-earth-900" data-id="element-102">
                To Support, Not To Replace
              </h2>
            </div>
            <div className="pl-0 md:pl-16" data-id="element-103">
              <div className="bg-sage-800 text-sage-50 p-8 rounded-3xl relative overflow-hidden" data-id="element-104">
                <ShieldCheck className="absolute -right-6 -bottom-6 w-48 h-48 text-sage-700 opacity-50" data-id="element-105" />
                <div className="relative z-10 space-y-6" data-id="element-106">
                  <p className="text-xl font-serif italic" data-id="element-107">
                    It is essential to understand that supplementation is meant
                    to support a good diet, not replace it.
                  </p>
                  <p className="text-lg text-sage-100" data-id="element-108">
                    The goal is not to "take everything," but to fill the gaps
                    that modern life creates.
                  </p>
                  <p className="text-lg text-sage-100" data-id="element-109">
                    As you will see in your Smart Supplement Guide, the secret
                    is strategy: knowing what is useful for you and choosing
                    quality over quantity.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </FadeIn>

        {/* Conclusion */}
        <FadeIn data-id="element-110">
          <section className="mt-32" data-id="element-111">
            <div className="border-t-2 border-sage-200 pt-16" data-id="element-112">
              <div className="text-center mb-10" data-id="element-113">
                <h2 className="font-serif text-3xl md:text-4xl font-semibold text-earth-900 mb-4" data-id="element-114">
                  The Importance of Choosing Wisely
                </h2>
                <p className="text-xl text-sage-600 italic font-serif" data-id="element-115">
                  It is not about consuming more — it is about consuming better.
                </p>
              </div>

              <div className="bg-white p-8 md:p-10 rounded-3xl shadow-sm border border-sage-100 text-center max-w-2xl mx-auto" data-id="element-116">
                <p className="text-lg text-earth-800 mb-8" data-id="element-117">
                  If you decide to add a supplement to your life, make sure it
                  is an investment, not an expense.
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-earth-900 font-medium" data-id="element-118">
                  <div className="flex items-center gap-2" data-id="element-119">
                    <CheckCircle2 className="w-5 h-5 text-sage-500" data-id="element-120" />
                    <span data-id="element-121">Transparent brands</span>
                  </div>
                  <div className="hidden sm:block w-1.5 h-1.5 rounded-full bg-sage-300" data-id="element-122"></div>
                  <div className="flex items-center gap-2" data-id="element-123">
                    <CheckCircle2 className="w-5 h-5 text-sage-500" data-id="element-124" />
                    <span data-id="element-125">Natural ingredients</span>
                  </div>
                  <div className="hidden sm:block w-1.5 h-1.5 rounded-full bg-sage-300" data-id="element-126"></div>
                  <div className="flex items-center gap-2" data-id="element-127">
                    <CheckCircle2 className="w-5 h-5 text-sage-500" data-id="element-128" />
                    <span data-id="element-129">Usable formulas</span>
                  </div>
                </div>

                <p className="mt-8 text-earth-800 italic" data-id="element-130">
                  Choose transparent brands, naturally sourced ingredients, and
                  formulas your body can truly recognize and use.
                </p>
              </div>
            </div>
          </section>
        </FadeIn>

        {/* CTA Box */}
        <FadeIn data-id="element-131">
          <section className="mt-20 mb-8" data-id="element-132">
            <a href="https://supplementblueprint.guias-salud.com" target="_blank" rel="noopener noreferrer" className="block group" data-id="element-133">
              <div className="bg-sage-800 hover:bg-sage-700 transition-colors duration-300 p-10 md:p-14 rounded-3xl text-center relative overflow-hidden cursor-pointer" data-id="element-134">
                <div className="absolute inset-0 bg-gradient-to-br from-sage-700/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-135"></div>
                <div className="relative z-10" data-id="element-136">
                  <p className="font-serif text-2xl md:text-3xl text-white font-semibold mb-3" data-id="element-137">
                    Want More Info?
                  </p>
                  <p className="text-sage-200 text-lg mb-8 max-w-lg mx-auto" data-id="element-138">
                    Discover the full Supplement Blueprint — your step-by-step
                    guide to smarter supplementation.
                  </p>
                  <span className="inline-flex items-center gap-2 bg-white text-sage-800 font-semibold px-8 py-4 rounded-full group-hover:gap-3 transition-all duration-300" data-id="element-139">
                    Yes, please
                    <ArrowRight className="w-5 h-5" data-id="element-140" />
                  </span>
                </div>
              </div>
            </a>
          </section>
        </FadeIn>
      </main>

      {/* Footer */}
      <footer className="border-t border-sage-200 py-10 px-6" data-id="element-141">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-142">
          <p className="text-sm text-earth-800/60" data-id="element-143">
            &copy; {new Date().getFullYear()} All rights reserved.
          </p>
          <div className="flex items-center gap-6" data-id="element-144">
            <button onClick={() => setShowPrivacy(true)} className="text-sm text-earth-800/60 hover:text-sage-600 transition-colors underline-offset-4 hover:underline" data-id="element-145">
              Privacy Policy
            </button>
            <button onClick={() => setShowTerms(true)} className="text-sm text-earth-800/60 hover:text-sage-600 transition-colors underline-offset-4 hover:underline" data-id="element-146">
              Terms of Service
            </button>
          </div>
        </div>
      </footer>

      {/* Privacy Modal */}
      <Modal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Privacy Policy" data-id="element-147">
        <p data-id="element-148">
          Your privacy is important to us. This Privacy Policy explains how we
          collect, use, and protect your personal information.
        </p>
        <p data-id="element-149">
          <strong data-id="element-150">Information We Collect:</strong> We may collect personal
          information you provide directly, such as your name and email address
          when you subscribe to our content or guides.
        </p>
        <p data-id="element-151">
          <strong data-id="element-152">How We Use Your Information:</strong> We use your information
          to deliver the content and services you request, improve our
          offerings, and communicate updates relevant to your interests.
        </p>
        <p data-id="element-153">
          <strong data-id="element-154">Data Protection:</strong> We implement appropriate security
          measures to protect your personal information from unauthorized
          access, alteration, or disclosure.
        </p>
        <p data-id="element-155">
          <strong data-id="element-156">Third Parties:</strong> We do not sell or share your personal
          information with third parties for marketing purposes without your
          explicit consent.
        </p>
        <p data-id="element-157">
          <strong data-id="element-158">Cookies:</strong> Our website may use cookies to enhance your
          browsing experience. You can manage cookie preferences through your
          browser settings.
        </p>
        <p data-id="element-159">
          <strong data-id="element-160">Your Rights:</strong> You have the right to access, correct,
          or delete your personal data at any time by contacting us.
        </p>
        <p data-id="element-161">
          This policy may be updated periodically. We encourage you to review it
          regularly.
        </p>
      </Modal>

      {/* Terms Modal */}
      <Modal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Terms of Service" data-id="element-162">
        <p data-id="element-163">
          By accessing and using this content, you agree to the following terms
          and conditions.
        </p>
        <p data-id="element-164">
          <strong data-id="element-165">Educational Purpose:</strong> All content provided is for
          informational and educational purposes only. It is not intended as
          medical advice, diagnosis, or treatment.
        </p>
        <p data-id="element-166">
          <strong data-id="element-167">No Medical Advice:</strong> The information in this guide
          should not replace professional medical consultation. Always consult a
          qualified healthcare provider before starting any supplementation
          regimen.
        </p>
        <p data-id="element-168">
          <strong data-id="element-169">Intellectual Property:</strong> All content, design, and
          materials are protected by copyright. You may not reproduce,
          distribute, or modify any content without prior written permission.
        </p>
        <p data-id="element-170">
          <strong data-id="element-171">Accuracy:</strong> While we strive to provide accurate and
          up-to-date information, we make no warranties regarding the
          completeness or reliability of the content.
        </p>
        <p data-id="element-172">
          <strong data-id="element-173">Limitation of Liability:</strong> We are not liable for any
          damages arising from the use of or reliance on the information
          provided in this guide.
        </p>
        <p data-id="element-174">
          <strong data-id="element-175">Changes:</strong> We reserve the right to modify these terms
          at any time. Continued use of the content constitutes acceptance of
          any changes.
        </p>
      </Modal>
    </article>;
}